import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Package } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/common/PageHero";
import HelpCTA from "@/components/common/HelpCTA";
import { Reveal } from "@/components/common/Reveal";
import PartnerLogo from "@/components/partner/PartnerLogo";
import { usePartners, useSite } from "@/context/SiteDataContext";

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const ALL = "ALL";

// Matches the fixed site header in Header.jsx. The alphabet bar sits directly
// beneath it and stays there while the list scrolls.
const HEADER_HEIGHT = 88;

/**
 * First letter a partner files under. A name starting with a digit or a symbol
 * groups under "#" rather than disappearing from an A–Z index entirely.
 */
const initialOf = (name) => {
  const first = String(name || "").trim().charAt(0).toUpperCase();
  return first >= "A" && first <= "Z" ? first : "#";
};

export default function Partners() {
  const PARTNERS = usePartners();
  const SITE = useSite();

  // A filter, not jump links into separate sections. Giving every letter its
  // own block left single cards stranded on rows of three and stretched the
  // list down the page; filtering keeps the grid full whatever is selected.
  const [selected, setSelected] = useState(ALL);

  const { sorted, available } = useMemo(() => {
    const rows = [...PARTNERS].sort((a, b) => a.name.localeCompare(b.name));
    return { sorted: rows, available: new Set(rows.map((p) => initialOf(p.name))) };
  }, [PARTNERS]);

  const visible = selected === ALL ? sorted : sorted.filter((p) => initialOf(p.name) === selected);

  // If the selected letter stops existing — a partner hidden in the CMS, say —
  // fall back to All rather than leaving an empty page with no explanation.
  useEffect(() => {
    if (selected !== ALL && !available.has(selected)) setSelected(ALL);
  }, [available, selected]);

  // The bar wraps to two rows on a phone and one on a desktop. Measuring it
  // keeps the results clear of it either way.
  const barRef = useRef(null);
  const [barHeight, setBarHeight] = useState(56);
  useEffect(() => {
    const measure = () => setBarHeight(barRef.current?.offsetHeight || 56);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const letterClass = (active, enabled) =>
    `w-7 h-7 inline-flex items-center justify-center rounded text-sm transition-colors ${
      active
        ? "bg-aqua text-white font-bold"
        : enabled
          ? "text-ink font-bold hover:bg-aqua/15"
          : "text-greyish/35 font-semibold cursor-default"
    }`;

  return (
    <>
      <Seo routeKey="partners" title="Our Partners" description="Athena Scientific works with leading global manufacturers including NEPTEC, EURPING, LabTech, Ohaus, Haier Biomedical and Kubota." canonical={`https://${SITE.website}/partners`} />
      <PageHero eyebrow="Our Partners" title="Leading global manufacturers, one trusted partner" description="We work closely with respected manufacturers to bring reliable laboratory technologies to your bench." crumbs={[{ label: "Our Partners" }]} />

      {/* Sticky, so the filter stays reachable once the visitor is into the
          list. Letters with no manufacturer are shown but inert: the alphabet
          stays visually complete and it is obvious at a glance where there is
          something to see. */}
      <nav
        ref={barRef}
        aria-label="Filter manufacturers by first letter"
        data-testid="partners-alphabet"
        className="sticky top-[88px] z-30 bg-white/95 backdrop-blur border-b border-border"
      >
        <div className="container-x py-3">
          <div className="flex flex-wrap items-center justify-center gap-x-1 gap-y-1.5">
            <button
              type="button"
              onClick={() => setSelected(ALL)}
              aria-pressed={selected === ALL}
              data-testid="partners-letter-all"
              className={`h-7 px-3 inline-flex items-center justify-center rounded text-sm transition-colors ${
                selected === ALL ? "bg-aqua text-white font-bold" : "text-ink font-bold hover:bg-aqua/15"
              }`}
            >
              All
            </button>
            <span aria-hidden="true" className="mx-1.5 h-5 w-px bg-border" />

            {available.has("#") && (
              <button
                type="button"
                onClick={() => setSelected("#")}
                aria-pressed={selected === "#"}
                data-testid="partners-letter-hash"
                className={letterClass(selected === "#", true)}
              >
                #
              </button>
            )}

            {ALPHABET.map((letter) => {
              const enabled = available.has(letter);
              return enabled ? (
                <button
                  key={letter}
                  type="button"
                  onClick={() => setSelected(letter)}
                  aria-pressed={selected === letter}
                  data-testid={`partners-letter-${letter}`}
                  className={letterClass(selected === letter, true)}
                >
                  {letter}
                </button>
              ) : (
                <span key={letter} aria-disabled="true" className={letterClass(false, false)}>
                  {letter}
                </span>
              );
            })}
          </div>
        </div>
      </nav>

      <section
        className="container-x py-14 md:py-20"
        // Clears the header and the bar above it, so a filtered view never
        // opens with its first card tucked underneath them.
        style={{ scrollMarginTop: `${HEADER_HEIGHT + barHeight + 16}px` }}
      >
        {/* Three across, as it was before the letters became sections. */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {visible.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 3) * 0.07}>
              {/* Centred: the logo already sits centred in its box, and a
                  left-aligned name underneath looked misaligned against it. */}
              <div data-testid={`partner-card-${p.slug}`} className="h-full flex flex-col items-center text-center p-8 rounded-lg border border-border bg-white hover:shadow-[0_12px_32px_rgba(0,0,0,0.06)] transition-all">
                <div className="w-full border-b border-border pb-6">
                  {p.logo && (
                    <div className="mb-4 flex justify-center">
                      <PartnerLogo name={p.name} logo={p.logo} />
                    </div>
                  )}
                  <h2 className="text-xl md:text-2xl font-extrabold tracking-tight text-ink">
                    {p.name}
                  </h2>
                </div>
                <p className="mt-6 text-greyish text-sm flex-1">{p.description}</p>
                <div className="mt-6 flex flex-wrap justify-center gap-3">
                  <a href={p.website} target="_blank" rel="noopener noreferrer" data-testid={`partner-website-${p.slug}`} className="inline-flex items-center gap-1.5 text-sm font-semibold border border-border px-4 py-2.5 rounded-md text-ink hover:border-ink/40 hover:bg-[#f6f8f9] transition-all">
                    Website <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                  {p.hasProducts && (
                    <Link to={`/products?brand=${p.brandSlug}`} data-testid={`partner-products-${p.slug}`} className="inline-flex items-center gap-1.5 text-sm font-semibold bg-aqua text-white px-4 py-2.5 rounded-md hover:bg-aqua-dark transition-all">
                      <Package className="w-3.5 h-3.5" /> View Products
                    </Link>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <HelpCTA />
    </>
  );
}
